import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-icon.svg';
import { ApiRequest } from '../../../interface/common.interface';
import { LedgerEntry } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableLedgerFilterApplied,
  fetchLedgerEntriesApi,
  fetchLedgerFiltersApi,
  setLedgerSortData,
  updateLedgerFilters,
} from '../../../redux/slices/TapCardLedgerSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import rfidApi from '../../../utils/api/rfid';

interface LedgerEntriesListProperties {
  onOpenDetailsModal?: (entry: LedgerEntry) => void;
  rightTopContent?: React.JSX.Element;
  sidePanelEnabled?: boolean;
  sidePanelManualContent?: React.JSX.Element;
  sidePanelTopLeftContent?: React.JSX.Element;
  onSidePanelClose?: () => void;
}

/**
 * Business Tap Card Ledger Entries list view component
 * @param {LedgerEntriesListProperties} properties - Component properties
 * @returns {React.JSX.Element} Business tap card ledger entries list view with CustomTable
 */
function LedgerEntriesList(properties: LedgerEntriesListProperties): React.JSX.Element {
  const {
    onOpenDetailsModal,
    rightTopContent,
    sidePanelEnabled,
    sidePanelManualContent,
    sidePanelTopLeftContent,
    onSidePanelClose,
  } = properties;
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    ledgerEntries,
    loading,
    limit,
    page,
    totalPages,
    totalResults,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.tapCardLedger);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchLedgerEntriesApi({
          page: 1,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableLedgerFilterApplied());
    }
  }, [isFilterUpdated, dispatch, filters, limit, sortBy, sortField]);

  /**
   * Refreshes the ledger entries list
   * @returns {void}
   */
  const handleRefresh = (): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchLedgerEntriesApi({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handles table sort change
   * @param {{ sortBy: 'asc' | 'desc'; sortField: string }} sortData - Sort data with field and order
   * @returns {void}
   */
  const handleSortChange = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setLedgerSortData(sortData));
    dispatch(
      fetchLedgerEntriesApi({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: 1,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and page limit change
   * @param {ApiRequest} value - Pagination parameters
   * @returns {void}
   */
  const handlePageLimitChange = (value: ApiRequest): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchLedgerEntriesApi({
        ...queryParameters,
        page: value.page ?? page,
        limit: value.limit ?? limit,
        sortBy,
        sortField,
      }),
    );
  };

  const columns = [
    {
      label: t('timestamp'),
      accessorKey: 'timestamp' as keyof LedgerEntry,
      // sortAllow: true,
      /**
       * Custom rendering for Timestamp
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Formatted timestamp string
       */
      customData: (row: LedgerEntry): React.JSX.Element => (
        <span
          role="presentation"
          className="rfid-table-timestamp hyperlink"
          onClick={() => {
            if (onOpenDetailsModal) {
              onOpenDetailsModal(row);
            }
          }}>
          {ConvertUTCtoDateTime(row.timestamp ?? '') ||
            ConvertUTCtoDateTime(row.createdAt ?? '') ||
            t('na')}
        </span>
      ),
    },
    {
      label: t('cardGroup'),
      accessorKey: 'cardPoolId' as keyof LedgerEntry,
      // sortAllow: true,
      /**
       * Custom rendering for Card Group
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Group cell
       */
      customData: (row: LedgerEntry): React.JSX.Element => {
        const hasDistinctName = Boolean(row.cardPoolName && row.cardPoolName !== row.cardPoolId);
        return (
          <div>
            <span>{row.cardPoolName || row.cardPoolId}</span>
            {hasDistinctName && <span className="rfid-table-group-cell__id">{row.cardPoolId}</span>}
          </div>
        );
      },
    },
    {
      label: t('tokenCard'),
      accessorKey: 'tokenId' as keyof LedgerEntry,
      // sortAllow: true,
      /**
       * Custom rendering for Transacted Card
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Token UID badge or dash
       */
      customData: (row: LedgerEntry): React.JSX.Element => {
        const tokenDisplay = row.tokenUid || row.tokenId;
        if (tokenDisplay) {
          return (
            <div className="rfid-table-token-cell">
              <span className="rfid-table-token-cell__uid">{tokenDisplay}</span>
            </div>
          );
        }
        return <span className="rfid-table-text-muted">{t('na')}</span>;
      },
    },
    {
      label: t('direction'),
      accessorKey: 'direction' as keyof LedgerEntry,
      // sortAllow: true,
      /**
       * Custom rendering for Direction
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Direction badge
       */
      customData: (row: LedgerEntry): React.JSX.Element => {
        const isCredit = row.direction === 'CREDIT';
        return (
          <span
            className={`rfid-direction-badge ${
              isCredit ? 'rfid-direction-badge--credit' : 'rfid-direction-badge--debit'
            }`}>
            <span className="numeric-unit" dir="ltr">
              {isCredit ? '+' : '-'}
            </span>{' '}
            <span>{isCredit ? t('credit') : t('debit')}</span>
          </span>
        );
      },
    },
    {
      label: t('energyKwh'),
      accessorKey: 'amountKwh' as keyof LedgerEntry,
      // sortAllow: true,
      /**
       * Custom rendering for Energy amount in kWh
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Energy amount
       */
      customData: (row: LedgerEntry): React.JSX.Element => {
        const isCredit = row.direction === 'CREDIT';
        let numericAmount = 0;
        const rawAmount = row.amountKwh as any;
        if (typeof rawAmount === 'object' && rawAmount !== null && '$numberDecimal' in rawAmount) {
          numericAmount = Number.parseFloat(rawAmount.$numberDecimal) || 0;
        } else if (typeof rawAmount === 'number') {
          numericAmount = rawAmount;
        } else if (typeof rawAmount === 'string') {
          numericAmount = Number.parseFloat(rawAmount) || 0;
        }

        const absAmount = Math.abs(numericAmount);
        const sign = isCredit ? '+' : '-';

        return (
          <div
            className={`rfid-table-energy-amount ${
              isCredit ? 'rfid-table-energy-amount--credit' : 'rfid-table-energy-amount--debit'
            }`}>
            <span className="numeric-unit" dir="ltr">
              {sign}
              {absAmount.toFixed(2)} kWh
            </span>
          </div>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof LedgerEntry,
      columnWidth: 80,
      // sortAllow: false,
      sticky: 'right' as const,
      /**
       * Action button for viewing ledger transaction details
       * @param {LedgerEntry} row - Current row
       * @returns {React.JSX.Element} Action buttons element
       */
      customData: (row: LedgerEntry): React.JSX.Element => (
        <div className="action-buttons-wrap">
          <div
            role="presentation"
            className="action-button"
            title={t('viewDetails')}
            onClick={(event) => {
              event.stopPropagation();
              if (onOpenDetailsModal) {
                onOpenDetailsModal(row);
              }
            }}>
            <EyeIcon className="icon view" />
          </div>
        </div>
      ),
    },
  ];

  const allExportColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('timestamp'), accessorKey: 'timestamp' },
    { label: t('cardGroup'), accessorKey: 'cardPoolId' },
    { label: t('tokenCard'), accessorKey: 'tokenId' },
    { label: t('direction'), accessorKey: 'direction' },
    { label: t('energyKwh'), accessorKey: 'amountKwh' },
  ];

  /**
   * Filter columns for export without hidden columns
   */
  const exportCurrentColumns = allExportColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export ledger entries data to CSV or PDF format
   * @param {string} type - Export file format ('csv' | 'pdf')
   * @returns {void}
   */
  const exportDataPdfOrCSV = (type: string): void => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    setExportLoader(true);
    rfidApi
      .fetchLedgerEntriesExport(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawResults =
          response?.data?.results ||
          response?.data?.data ||
          (Array.isArray(response?.data) ? response?.data : []);
        const dataToExport =
          Array.isArray(rawResults) && rawResults.length > 0 ? rawResults : ledgerEntries;

        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = dataToExport.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);
              case 'timestamp':
                return (
                  ConvertUTCtoDateTime(row.timestamp) ||
                  ConvertUTCtoDateTime(row.createdAt) ||
                  t('na')
                );
              case 'cardPoolId':
                return row.cardPoolName || row.cardPoolId || t('na');
              case 'tokenId':
                return row.tokenUid || row.tokenId || t('na');
              case 'direction':
                return row.direction === 'CREDIT' ? t('credit') : t('debit');
              case 'amountKwh': {
                const isCredit = row.direction === 'CREDIT';
                let numericAmount = 0;
                const rawAmount = row.amountKwh;
                if (
                  typeof rawAmount === 'object' &&
                  rawAmount !== null &&
                  '$numberDecimal' in rawAmount
                ) {
                  numericAmount = Number.parseFloat(rawAmount.$numberDecimal) || 0;
                } else if (typeof rawAmount === 'number') {
                  numericAmount = rawAmount;
                } else if (typeof rawAmount === 'string') {
                  numericAmount = Number.parseFloat(rawAmount) || 0;
                }
                return `${isCredit ? '+' : '-'}${numericAmount.toFixed(2)} kWh`;
              }
              default:
                return String((row as any)[col.accessorKey] || t('na'));
            }
          }),
        );

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'tap-card-ledger.csv');
          } else {
            exportPDF(headers, rows, 'tap-card-ledger.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('failedToExportTapCardLedgerData'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 500,
          }),
        );
      });
  };

  return (
    <CustomTable
      version="v2"
      header={columns}
      data={ledgerEntries}
      loading={loading}
      limit={limit}
      page={page}
      totalPages={totalPages}
      totalResults={totalResults}
      serialNo
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortChange}
      pageLimitChange={handlePageLimitChange}
      filters={filters}
      filtersToDispatch={updateLedgerFilters}
      filterListToDispatch={fetchLedgerFiltersApi}
      filterListLoading={filterListLoading}
      refreshRequired
      handleRefresh={handleRefresh}
      refershCurrentPages={handleRefresh}
      hoverLabel={t('refresh')}
      sizeChangeButtonRequired
      rightTopContent={rightTopContent}
      sidePanelEnabled={sidePanelEnabled}
      sidePanelManualContent={sidePanelManualContent}
      sidePanelTopLeftContent={sidePanelTopLeftContent}
      onSidePanelClose={onSidePanelClose}
      onHiddenColumnsChange={setHiddenColumns}
      exportDataPdfOrCSV={exportDataPdfOrCSV}
      exportLoader={exportLoader}
      exportData={ledgerEntries}
      isExportRequired
    />
  );
}

export default LedgerEntriesList;
